import React, { useState, useEffect, useMemo } from 'react'
import { getApfFieldsOptions } from 'uiComponents/filter/filterHelpers'
import { MetaData, FilterCategory } from 'uiComponents/filter/schema'
import Filter from 'uiComponents/filter'
import { useAppSelector } from 'store/hooks'
import { getActiveAccount } from 'preferences/selectors'
import { formatFlatOptions } from 'uiComponents/filter/filterHelpers'
import { useHistory } from 'react-router-dom'
import { renderSearch } from 'navigation'
import { ApfFieldsResponse } from 'orders/ordersService'
import { useGetAccountLocationsQuery } from 'orders/reduxQueries'
import {
    useGetAllProductsForSelect,
    useGetFullProductsAndArticlesNestedTree,
    useIsLoadingCategoriesOrArticles,
} from 'products/redux'
import ComboboxFilter from 'uiComponents/combobox/comboboxFilter'
import { useTranslation } from 'translations'
import './dailyScheduleFilters.scss'

const DAILY_SCHEDULE_STORAGE_KEY = 'daily_schedule_filters'
export const applicableFilters: FilterCategory[] = ['apf_fields', 'products', 'location_id']

const DailyScheduleFilters = ({
    apfFields,
    apfFieldsLoading,
}: {
    apfFields: ApfFieldsResponse
    apfFieldsLoading: boolean
}) => {
    const { t } = useTranslation()
    const accountSlug = useAppSelector(getActiveAccount)
    if (!accountSlug) throw new Error('No account selected')
    const history = useHistory()
    const { isLoading: productsLoading } = useGetFullProductsAndArticlesNestedTree()
    const { data: locationData, isLoading: locationsLoading } = useGetAccountLocationsQuery({ accountSlug })
    const [isInitialized, setIsInitialized] = useState(false)
    const productOptions = useGetAllProductsForSelect()
    const isLoadingCategoriesOrArticles = useIsLoadingCategoriesOrArticles()

    useEffect(() => {
        const filterParam = new URLSearchParams(location.search).get('filter')

        if (filterParam) {
            localStorage.setItem(DAILY_SCHEDULE_STORAGE_KEY, filterParam)
        } else {
            if (isInitialized) {
                localStorage.removeItem(DAILY_SCHEDULE_STORAGE_KEY)
            }
        }
    }, [location.search])

    useEffect(() => {
        setIsInitialized(true)
        const storedValue = localStorage.getItem(DAILY_SCHEDULE_STORAGE_KEY)
        if (storedValue) {
            history.replace({
                pathname: location.pathname,
                search: renderSearch({ filter: storedValue }),
            })
        }
    }, [])

    const data = useMemo((): MetaData[] => {
        const apfFieldsCategory = {
            category: 'apf_fields' as FilterCategory,
            options: getApfFieldsOptions(apfFields),
        }

        const locationCategory = {
            category: 'location_id' as FilterCategory,
            options: formatFlatOptions(
                'location_id',
                (locationData ?? []).map((l) => ({
                    name: l.name,
                    slug: l.uuid,
                })),
            ),
        }

        return [apfFieldsCategory, locationCategory]
    }, [apfFields, locationData])

    return (
        <div className="daily-schedule-filters">
            <div className="daily-schedule-filters__products">
                <ComboboxFilter
                    filterCategory="products"
                    options={productOptions}
                    loading={isLoadingCategoriesOrArticles}
                    limitTags={5}
                    searchPlaceholder={t('orders.dailySchedule.filterByProduct')}
                />
            </div>
            <div className="daily-schedule-filters__more">
                <Filter
                    accountSlug={accountSlug}
                    loading={productsLoading || apfFieldsLoading || locationsLoading}
                    applicableFilters={applicableFilters}
                    metaData={data}
                    standaloneFilters={['location_id', 'apf_fields']}
                    singleSelectCategories={['location_id']}
                    hideLabel
                />
            </div>
        </div>
    )
}

export default DailyScheduleFilters
